export const DEFAULT_TAG_COLOR = '#57606a';

export const DEFAULT_TAG_COLORS = [
  '#b60205',
  '#d93f0b',
  '#fbca04',
  '#0e8a16',
  '#006b75',
  '#1d76db',
  '#5319e7',
  '#e99695',
  '#f9d0c4',
  '#fef2c0',
  '#c2e0c6',
  '#bfdadc',
  '#c5def5',
  '#d4c5f9',
];

const HEX_SHORT = /^#([0-9a-fA-F]{3})$/;
const HEX_LONG = /^#([0-9a-fA-F]{6})$/;
const SENTINEL_HEX = '#123456';

const toHexByte = value =>
  `0${Math.max(0, Math.min(255, Math.round(value))).toString(16)}`.slice(-2);

export function normalizeHexColor(value) {
  if (!value || typeof value !== 'string') return null;

  let hex = value.trim();
  if (!hex) return null;
  if (hex.charAt(0) !== '#') hex = `#${hex}`;

  const shortMatch = hex.match(HEX_SHORT);
  if (shortMatch) {
    const [r, g, b] = shortMatch[1].split('');
    hex = `#${r}${r}${g}${g}${b}${b}`;
  }

  if (HEX_LONG.test(hex)) return hex.toLowerCase();

  return null;
}

function rgbStringToHex(value) {
  const normalized = normalizeHexColor(value);
  if (normalized) return normalized;

  const match = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(value);
  if (!match) return null;

  return `#${toHexByte(match[1])}${toHexByte(match[2])}${toHexByte(match[3])}`;
}

export function parseColorToHex(value) {
  const normalized = normalizeHexColor(value);
  if (normalized) return normalized;
  if (!value || typeof value !== 'string') return null;
  if (typeof document === 'undefined') return null;

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = SENTINEL_HEX;
    ctx.fillStyle = value.trim();
    const computed = rgbStringToHex(ctx.fillStyle);
    if (!computed) return null;

    if (computed === SENTINEL_HEX && normalizeHexColor(value) !== SENTINEL_HEX) {
      return null;
    }

    return computed;
  } catch {
    return null;
  }
}

export function resolveTagColor(color) {
  return parseColorToHex(color) || DEFAULT_TAG_COLOR;
}

export function getRandomHexColor() {
  const hex = `000000${Math.floor(Math.random() * 0xffffff).toString(16)}`.slice(-6);
  return `#${hex}`;
}

function hexToRgb(hex) {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;

  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

export function hexToRgba(hex, alpha) {
  const rgb = hexToRgb(hex) || hexToRgb(DEFAULT_TAG_COLOR);
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function getLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const toLinear = channel => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
}

function darkenHex(hex, factor) {
  const rgb = hexToRgb(hex);
  if (!rgb) return DEFAULT_TAG_COLOR;

  return `#${toHexByte(rgb.r * factor)}${toHexByte(rgb.g * factor)}${toHexByte(rgb.b * factor)}`;
}

export function getContrastInk(hex) {
  return getLuminance(hex) >= 0.45 ? '#1f2328' : '#ffffff';
}

export function getTagColorStyles(color) {
  const resolved = resolveTagColor(color);
  let ink = resolved;
  let current = resolved;
  let safety = 0;

  while (getLuminance(current) > 0.55 && safety < 8) {
    current = darkenHex(current, 0.72);
    ink = current;
    safety += 1;
  }

  return {
    color: ink,
    borderColor: ink,
    backgroundColor: hexToRgba(resolved, 0.15),
  };
}

export function getTagDisplay(tag) {
  if (!tag || typeof tag !== 'object') {
    return { name: '', color: DEFAULT_TAG_COLOR, description: '' };
  }

  const extra = tag.extra && typeof tag.extra === 'object' ? tag.extra : {};

  return {
    name:
      tag.displayName ||
      tag.name ||
      tag.title ||
      extra.displayName ||
      extra.name ||
      extra.title ||
      '',
    color: tag.color || extra.color,
    description: tag.description || tag.subtitle || extra.description || extra.subtitle || '',
  };
}
